import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  FormHelperText,
} from '@mui/material';
import { NorthEast, East, SouthEast } from '@mui/icons-material';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';
import { MaxLengthTextField } from '../../../../components/text-field';
import { DatePicker } from '../../../../components/date-picker/date-picker';
import { MethodTypeModel } from '../../../../api-service/clients';
import { FormDialog } from '../../../../components/form-dialog/form-dialog';
import { DialogSupportProvider } from '../../common/dialog-support/dialog-support-context';
import { DialogSupportBar } from '../../common/dialog-support/dialog-support-bar';
import { GenerateSummaryResult } from '../../common/dialog-support/dialog-support-interfaces';

export const PROGRESSION_OPTIONS = [
  { value: 1, label: 'Fremgang' },
  { value: 0, label: 'Uændret' },
  { value: -1, label: 'Tilbagegang' },
] as const;

export type ProgressionValue = 1 | 0 | -1;

interface FollowupValues {
  progression: ProgressionValue;
  description: string;
  eventDate: Date;
  methodTypeId?: string;
}

interface FollowupDialogProps {
  open: boolean;
  isEdit: boolean;
  initialValues?: Partial<FollowupValues>;
  methodTypes?: MethodTypeModel[];
  assignmentStartDate?: Date;
  partialObjectiveMethodType?: string;
  interventionObjectiveTitle?: string;
  interventionObjectiveDescription?: string;
  partialObjectiveTitle?: string;
  partialObjectiveDescription?: string;
  /** When true, the date picker is rendered read-only (the date is fixed by the event's start date). */
  eventDateReadOnly?: boolean;
  /** When set, the dialog offers dialogstøtte (recording + AI-generated followup text appended to the description). */
  generateSummary?: (transcriptionSessionId: string) => Promise<GenerateSummaryResult>;
  onClose: () => void;
  onSave: (values: FollowupValues) => void;
}

export function FollowupDialog({
  open,
  isEdit,
  initialValues,
  methodTypes = [],
  assignmentStartDate,
  partialObjectiveMethodType,
  interventionObjectiveTitle,
  interventionObjectiveDescription,
  partialObjectiveTitle,
  partialObjectiveDescription,
  eventDateReadOnly = false,
  generateSummary,
  onClose,
  onSave,
}: FollowupDialogProps) {
  const [progression, setProgression] = useState<ProgressionValue>(0);
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState<Date | undefined>(new Date());
  const [methodTypeId, setMethodTypeId] = useState<string>('');
  const [eventDateValidationError, setEventDateValidationError] = useState<string | undefined>();
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  // Calculate date constraints
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today
  const minDate = assignmentStartDate ? new Date(assignmentStartDate) : undefined;

  useEffect(() => {
    if (open) {
      setProgression((initialValues?.progression ?? 0) as ProgressionValue);
      setDescription(initialValues?.description ?? '');
      setEventDate(initialValues?.eventDate ?? new Date());
      setMethodTypeId(initialValues?.methodTypeId ?? partialObjectiveMethodType ?? '');
    }
  }, [open, initialValues, partialObjectiveMethodType]);

  // Validation logic
  const isEventDateValid = eventDate !== undefined && !eventDateValidationError;
  const isMethodTypeRequired = !!partialObjectiveMethodType;
  const isMethodTypeValid = !isMethodTypeRequired || !!methodTypeId;
  const normalizedFollowupMethodType = methodTypeId || undefined;
  const normalizedPoMethodType = partialObjectiveMethodType || undefined;
  const methodTypeChanged = normalizedFollowupMethodType !== normalizedPoMethodType;
  const isDescriptionRequired = methodTypeChanged;
  const isDescriptionValid = !isDescriptionRequired || description.trim().length > 0;
  const isValid = isEventDateValid && isMethodTypeValid && isDescriptionValid && !isGeneratingSummary;

  const methodTypeHelperText = useMemo(() => {
    if (!isMethodTypeRequired) return undefined;
    if (!methodTypeId) return 'Påkrævet da delmålet har en metode';
    return undefined;
  }, [isMethodTypeRequired, methodTypeId]);

  const descriptionHelperText = useMemo(() => {
    if (!methodTypeChanged) return undefined;
    return 'Påkrævet når du vælger en anden metode end delmålets metode';
  }, [methodTypeChanged]);

  function handleEventDateChange(value: Date | string | undefined) {
    if (value instanceof Date) {
      setEventDate(value);
    } else if (typeof value === 'string') {
      setEventDate(new Date(value));
    } else {
      setEventDate(undefined);
    }
  }

  function handleSave() {
    if (!isValid) return;
    onSave({
      progression,
      description: description.trim(),
      eventDate,
      methodTypeId: methodTypeId || undefined,
    });
  }

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Rediger opfølgning' : 'Tilføj opfølgning'}
      confirmButtonText={isEdit ? 'Opdater' : 'Tilføj'}
      onConfirmClick={handleSave}
      cancelButtonText="Afbryd"
      onCancelClick={onClose}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      isValid={isValid}
      dontCapitalizeButtonTexts
      disableBackdropClick
    >
      <Box mt={1} display="flex" flexDirection="column" gap={2}>
        {interventionObjectiveTitle && (
          <Box>
            <Typography variant="body2">
              <strong>Indsatsmål:</strong> {interventionObjectiveTitle}
            </Typography>
            {interventionObjectiveDescription && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {interventionObjectiveDescription}
              </Typography>
            )}
          </Box>
        )}
        {partialObjectiveTitle && (
          <Box>
            <Typography variant="body2">
              <strong>Delmål:</strong> {partialObjectiveTitle}
            </Typography>
            {partialObjectiveDescription && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {partialObjectiveDescription}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      {generateSummary && (
        <Box mt={2}>
          <DialogSupportProvider>
            <DialogSupportBar
              generateSummary={generateSummary}
              generateButtonLabel="Dan opfølgning"
              onSummaryGenerated={summary => setDescription(prev => (prev ? `${prev}\n\n${summary}` : summary))}
              onGeneratingChange={setIsGeneratingSummary}
            />
          </DialogSupportProvider>
        </Box>
      )}
      <Box mt={2} display="flex" flexDirection="column" gap={1}>
        <Row>
          <Cell size="full">
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Progression
              </Typography>
              <ToggleButtonGroup
                value={progression}
                exclusive
                onChange={(e, value) => {
                  if (value !== null) {
                    setProgression(value as ProgressionValue);
                  }
                }}
                fullWidth
              >
                <ToggleButton value={1} aria-label="fremgang">
                  <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                    <NorthEast />
                    <Typography variant="caption">Fremgang</Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton value={0} aria-label="uændret">
                  <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                    <East />
                    <Typography variant="caption">Uændret</Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton value={-1} aria-label="tilbagegang">
                  <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                    <SouthEast />
                    <Typography variant="caption">Tilbagegang</Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Cell>
        </Row>
        <Row>
          <Cell size="full">
            <MaxLengthTextField
              label="Beskrivelse"
              maxLength={5000}
              maxLengthError="Må ikke overskride 5000 tegn"
              value={description}
              onChange={e => setDescription(e.currentTarget.value)}
              fullWidth
              variant="outlined"
              multiline
              minRows="8"
              disabled={isGeneratingSummary}
              required={isDescriptionRequired}
              error={isDescriptionRequired && !isDescriptionValid}
              helperText={descriptionHelperText}
            />
          </Cell>
        </Row>
        <Row>
          <Cell size={4}>
            <DatePicker
              label="Dato"
              value={eventDate}
              onChange={handleEventDateChange}
              onInternalValidation={setEventDateValidationError}
              minDate={eventDateReadOnly ? undefined : minDate}
              maxDate={eventDateReadOnly ? undefined : today}
              fullWidth
              required
              error={!isEventDateValid}
              enableRelativeDateFormatting
              readOnly={eventDateReadOnly}
              disabled={eventDateReadOnly}
            />
          </Cell>
          <Cell size={8}>
            <FormControl fullWidth variant="outlined" error={isMethodTypeRequired && !methodTypeId}>
              <InputLabel shrink>Metode</InputLabel>
              <Select value={methodTypeId} label="Metode" onChange={e => setMethodTypeId(e.target.value)} displayEmpty notched>
                <MenuItem value="">
                  <em>Ingen</em>
                </MenuItem>
                {methodTypes.map(mt => (
                  <MenuItem key={mt.id} value={mt.id}>
                    {mt.name}
                  </MenuItem>
                ))}
              </Select>
              {methodTypeHelperText && <FormHelperText>{methodTypeHelperText}</FormHelperText>}
            </FormControl>
          </Cell>
        </Row>
      </Box>
    </FormDialog>
  );
}
